import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import without from 'lodash/without'
import uniq from 'lodash/uniq'
import moment from 'moment'
import merge from 'lodash/merge'
import uuid from 'uuid/v1'

import { MERGE_ENTITIES } from 'reducers/entityReducers'
import { GET, POST } from 'actions/apiActions'
import {
  addAndReplacePageState,
  replacePageState,
  addToPageState,
  removeFromPageState,
} from 'actions/pageStateActions'
import { getQueryValue, getActiveHash } from 'selectors/pageStateSelectors'
import { STATUS as S, INTENTS } from 'reducers/quoteReducers'
import { getEntity } from 'selectors/entitySelectors'
import { shouldTrack, getIsLoggedIn } from 'selectors/authSelectors'
import { isKnownCustomer } from 'selectors/naughtySelectors'

import {
  getEnableDealerPolicyLink,
  getRooftopName,
} from 'selectors/appStatusSelectors'
import {
  getQuote,
  getQuoteByVersion,
  getQuoteValue,
  getQuoteForProgramPull,
  getBestPayment,
  getBestProgram,
  getBestProgramKey,
  getBestProgramValue,
  getRebatesForVehicle,
  getQuoteTrackerStats,
  getRebateById,
  findNearestProgram,
  getLocks,
  getBestAllAroundProgram,
  generateSelectedRebateIds,
  getStartingMode,
  getRebatesForCategory,
  getSelectedRebateIds,
  shouldQuoteHaveTaxes,
  shouldQuoteHaveFees,
  doesPrepencilExist,
  getPreselectedRebates,
} from 'selectors/quoteSelectors'

import { getTooSqueezed } from 'selectors/naughtySelectors'

import {
  track,
  tagCustomer,
  QUOTE_CHANGED,
  QUOTE_ADDITION,
  QUOTE_REMOVAL,
  QUOTE_REFRESHED,
  QUOTE_CHANGE_MATCHERS,
  QUOTE_SAVED,
  QUOTE_REACTION,
} from 'actions/naughtyActions'

import { SET_SHORT_CODE_QUOTE_ID } from 'reducers/appStatusReducers'

import {
  UPDATE_GLOBAL_QUOTE_VALUE,
  STATUS,
  initialAdd,
  initialTrade,
  initialFee,
} from 'reducers/quoteReducers'

import { trackView, setNewStartingQuoteId } from 'actions/inventoryActions'
import {
  getShouldIncludeTaxesSetting,
  getShouldIncludeFeesSetting,
} from 'selectors/appStatusSelectors'

const INITIAL_TYPES = {
  additions: initialAdd,
  warranties: initialAdd,
  maintenancePlans: initialAdd,
  trades: initialTrade,
  fees: initialFee,
}

const CLEAN_LIST = [
  // TODO whitelist v. blacklist?
  'mode',
  'dirty',
  'status',
  'intent',
  'term',
  'loanTerm',
  'leaseTerm',
  'creditScore',
  'miles',
  'gettingPrograms',
  'programs',
  'down',
  'rebateInfo', // here because this is lazy loaded and would cause the quote to dirty immediately
  'customer',
  'customer.firstName',
  'customer.lastName',
  'customer.email',
  'customer.phone',
  'desiredPayment',
  'customer.approveTab',
  'customer.selectedSection',
  'rollResults',
  'expirationDate',
  'expirations',
  'expirationDays',
  'shortCode',
  'createdBy',
  'locks.mode',
  'locks.term',
  'locks.down',
  'locks.miles',
  'locks.creditScore',
  'viewMode',
  'includeTaxes',
  'includeFees',
  'programsHaveTaxesAndFees',
  'programsHaveTaxes',
  'programsHaveFees',
  'customerComments',
  'appointment.type',
  'appointment.date',
  'appointment.time',
  'isLoading',

  //'taxRegionId',
  //'stateFeeTax',
]

const REMOVE_ON_SAVE = ['_versions']

const getClonedQuote = (state, vId, qId, mode, extra) =>
  cloneDeep(getQuote(state, vId, qId, mode, extra))

export const changeQuote = (vId, qId, properties, options = {}) => dispatch => {
    dispatch({
      type: 'CHANGE_QUOTE',
      payload: { vId, qId, properties, options },
    })

    let {
      propagate,
      clean,
      dirty,
      forceProgramUpdate,
      track = true,
      squeeze,
      makeNewId,
    } = options

    return dispatch(
      updateQuote(
        vId,
        qId,
        properties,
        propagate,
        clean,
        dirty,
        forceProgramUpdate,
        track,
        squeeze,
        makeNewId,
      ),
    )
  },
  updateQuote = (
    vId,
    qId,
    properties = {},
    propagate,
    clean,
    dirty,
    forceProgramUpdate,
    track = true,
    squeeze,
    makeNewId,
  ) => async (dispatch, getState) => {
    if (!qId) return

    if (makeNewId) {
      qId = uuid()
      dispatch(addToPageState({ quoteId: qId }))
    }

    if (typeof propagate === 'object') throw JSON.stringify(properties)

    let tooSqueezed = await getTooSqueezed(getState()),
      pd = getClonedQuote(getState(), vId, qId),
      dirtyFlag = false,
      isNew = get(getEntity(getState(), vId), 'type') === 'New',
      propKeys = Object.keys(properties),
      trackerVals = []

    track = track && shouldTrack(getState())

    if (track && squeeze && tooSqueezed) {
      dispatch(
        addToPageState({
          quoteAction: INTENTS.contact,
          mode: getQuoteValue(getState(), vId, qId, 'mode'),
        }),
      )

      return
    }

    propKeys.forEach(k => {
      if (!clean && !CLEAN_LIST.includes(k)) {
        dirtyFlag = true
      }

      let matchKey = Object.keys(QUOTE_CHANGE_MATCHERS).find(m =>
          QUOTE_CHANGE_MATCHERS[m].matcher(k),
        ),
        match = matchKey ? QUOTE_CHANGE_MATCHERS[matchKey] : null

      if (match && match.context) {
        trackerVals = [...trackerVals, match.context(k, properties, pd)].filter(
          x => x,
        )
      }

      !isEqual(properties[k], pd[k]) && set(pd, k, properties[k])
    })

    // TODO need some kind of universal pre/post processor for things like this?
    if (
      !pd.hasOwnProperty('desiredFrontProfit') ||
      propKeys.includes('invoice') ||
      propKeys.includes('cost') ||
      propKeys.includes('price')
    ) {
      set(pd, 'desiredFrontProfit', pd.price - (isNew ? pd.invoice : pd.cost))
    }

    if (propKeys.includes('desiredFrontProfit')) {
      set(pd, 'price', (isNew ? pd.invoice : pd.cost) + pd.desiredFrontProfit)
      set(pd, 'desiredFrontProfit', properties.desiredFrontProfit)
    }

    if (propKeys.includes('customerZipCode')) {
      if (!propKeys.includes('rateMarket')) {
        // zip code changed independently of the rateMarket
        dispatch(syncRateMarket(vId, qId, properties.customerZipCode, clean))
      }

      // this can result in double rebate calls on page load
      dispatch(getRebatesForQuote(vId, qId, properties.customerZipCode))
    }
    // TODO end

    if (!clean && (dirty || dirtyFlag)) {
      set(pd, 'dirty', true)
      set(pd, 'rollResults', null)
    } else {
      set(pd, 'rollResults', properties.rollResults || null)
    }

    let ret = dispatch({
      type: MERGE_ENTITIES,
      payload: { [qId]: pd },
    })

    properties.mode &&
      getActiveHash(getState()) !== properties.mode &&
      dispatch(replacePageState({}, properties.mode, true))

    propagate && dispatch(updateGlobalQuoteValue(properties)) // TODO global pencil is deprecated

    if (forceProgramUpdate) ret = dispatch(getProgramsForQuote(vId, qId, true))

    track &&
      trackerVals &&
      dispatch(trackQuoteChange(vId, qId, trackerVals, squeeze))

    // if trades are entered, throw it in the tag
    if (propKeys.some(k => k.includes('trades')))
      dispatch(updateCustomerTrades(vId, qId))

    return ret
  },
  updateCustomerTrades = (vId, qId) => (dispatch, getState) => {
    dispatch(
      tagCustomer(vId, qId, true, {
        trades: getQuoteValue(getState(), vId, qId, 'trades'),
      }),
    )
  },
  syncRateMarket = (vId, qId, customerZipCode) => (dispatch, getState) => {
    dispatch({ type: 'SYNC_RATE_MARKET' })
    dispatch(changeQuote(vId, qId, { gettingPrograms: true }))
    const oldStateFeeTax = getQuoteValue(getState(), vId, qId, 'stateFeeTax'),
      oldRateMarket = getQuoteValue(getState(), vId, qId, 'rateMarket'),
      oldTaxRegionId = getQuoteValue(getState(), vId, qId, 'taxRegionId')

    return dispatch(validateZip(customerZipCode)).then(rateMarket => {
      if (!isNaN(parseInt(rateMarket, 10)))
        dispatch(
          changeQuote(
            vId,
            qId,
            {
              customerZipCode,
              rateMarket,
              stateFeeTax: rateMarket === oldRateMarket ? oldStateFeeTax : null,
              taxRegionId: rateMarket === oldRateMarket ? oldTaxRegionId : null,
              gettingPrograms: false,
            },
            { clean: true },
          ),
        )
    })
  },
  trackQuoteChange = (vId, qId, changes = [], free) => (dispatch, getState) => {
    if (isEmpty(changes)) return

    dispatch(
      track(
        QUOTE_CHANGED,
        {
          ...getQuoteTrackerStats(getState(), vId, qId),
          changes,
        },
        free,
      ),
    )
  },
  setMode = op => dispatch => {
    dispatch(
      changeQuote(
        op.vehicleId,
        op.quoteId,
        { mode: op.mode },
        { squeeze: true },
      ),
    )
  },
  setViewMode = op => dispatch => {
    dispatch(changeQuote(op.vehicleId, op.quoteId, { viewMode: op.viewMode }))
  },
  updateCustomerZip = (vId, qId, customerZipCode) => dispatch => {
    dispatch(changeQuote(vId, qId, { customerZipCode }, { squeeze: true }))

    dispatch(tagCustomer(vId, qId, { zipCode: customerZipCode }))
    dispatch(getProgramsForQuote(vId, qId, true))
  },
  selectBestAllAroundProgram = (vId, qId) => (dispatch, getState) => {
    let program = getBestAllAroundProgram(getState(), vId, qId),
      { mode, term, down, creditScore, miles, Payment } = program,
      p = v => parseFloat(v, 10)

    dispatch(
      changeQuote(
        vId,
        qId,
        {
          desiredPayment: Payment,
          mode,
          [`${mode}Term`]: p(term),
          down: p(down),
          creditScore: p(creditScore),
          miles: p(
            !isEmpty(miles)
              ? miles
              : getQuoteValue(getState(), vId, qId, 'miles'),
          ),
        },
        { squeeze: true },
      ),
    )
  },
  updateQuoteSlider = (vId, qId, payment, apply) => (dispatch, getState) => {
    let locks = getLocks(getState(), vId, qId),
      nearest = findNearestProgram(getState(), vId, qId, null, payment, locks),
      { mode, term, down, creditScore, miles } = nearest,
      p = v => parseFloat(v, 10),
      programData = {
        mode,
        [`${mode}Term`]: p(term),
        down: p(down),
        creditScore: p(creditScore),
        miles: p(
          !isEmpty(miles)
            ? miles
            : getQuoteValue(getState(), vId, qId, 'miles'),
        ),
      }

    dispatch(
      changeQuote(vId, qId, {
        desiredPayment: payment,
        ...(apply ? programData : {}),
      }),
    )
  },
  resetQuote = (vId, qId) => (dispatch, getState) => {
    let includeTaxes = getShouldIncludeTaxesSetting(getState()),
      includeFees = getShouldIncludeFeesSetting(getState())

    dispatch({ type: 'RESET_QUOTE', payload: { includeTaxes, includeFees } })
    dispatch(
      changeQuote(
        vId,
        qId,
        {
          customer: {},
          appointment: {},
          includeTaxes,
          includeFees,
          programsHaveTaxes: false,
          programsHaveFees: false,
        },
        {
          clean: true,
          track: true,
          forceProgramUpdate: true,
          makeNewId: true,
        },
      ),
    )
  },
  createQuote = (
    vId,
    qId,
    addendum = {},
    forceProgramUpdate,
    replacePageState,
  ) => async (dispatch, getState) => {
    dispatch({ type: 'CREATE_QUOTE', payload: { qId } })

    if (isEmpty(qId)) return

    replacePageState && dispatch(addAndReplacePageState({ quoteId: qId }))

    dispatch(changeQuote(vId, qId, { isLoading: true }))

    return dispatch(GET.quote({}, { uuid: qId }))
      .then(async (response = {}) => {
        let quote = {
          ...getQuote(getState(), vId, qId), // get the prepencil
          ...response, // merge in the response
          ...addendum, // and any extras
        }

        let customer = get(quote, 'customer', {}),
          autosave = getQueryValue(getState(), 'autosave'),
          newQuote = isEmpty(response.shortCode),
          isKnown = await isKnownCustomer(getState()),
          hasTaxesAlready =
            quote.includeTaxes || quote.programsHaveTaxes || false,
          hasFeesAlready = quote.includeFees || quote.programsHaveFees || false,
          quoteShouldHaveTaxes = await shouldQuoteHaveTaxes(
            getState(),
            vId,
            qId,
          ),
          quoteShouldHaveFees = await shouldQuoteHaveFees(getState(), vId, qId),
          hasTrades =
            get(quote, 'trades', []).length > 0 &&
            (get(quote, 'trades[0].allowance', 0) ||
              get(quote, 'trades[0].payoff', 0)),
          hasRebateCats = !isEmpty(quote.selectedRebateCategories),
          customerIsDifferent = !isEqual(response.customer, customer)

        forceProgramUpdate = false

        // override zip and rateMarket if exists
        quote.customerZipCode = customer.zipCode || quote.customerZipCode
        quote.rateMarket = customer.rateMarket || quote.rateMarket

        // figure out if we should pull fresh programs
        if (newQuote) {
          quote.includeFees = hasFeesAlready || quoteShouldHaveFees
          quote.includeTaxes = hasTaxesAlready || quoteShouldHaveTaxes

          forceProgramUpdate =
            forceProgramUpdate ||
            hasTrades ||
            hasRebateCats ||
            (!hasTaxesAlready && quoteShouldHaveTaxes) ||
            (!hasFeesAlready && quoteShouldHaveFees) ||
            isKnown
        }

        forceProgramUpdate = forceProgramUpdate || customerIsDifferent

        quote.gettingPrograms = forceProgramUpdate
        quote.programsHaveTaxes = quote.programsHaveTaxes || hasTaxesAlready
        quote.programsHaveFees = quote.programsHaveFees || hasFeesAlready

        return dispatch(
          getRebatesForQuote(vId, qId, quote.customerZipCode, quote, {
            clean: true,
            track: false,
            forceProgramUpdate,
            processRebateCats: hasRebateCats,
          }),
        )
          .then(() => {
            // tag & bag
            dispatch(
              tagCustomer(vId, qId, true, {
                ...customer,
                quotes: { ...get(tagCustomer, 'quotes', {}), [vId]: qId },
                zipCode: quote.customerZipCode,
                rateMarket: quote.rateMarket,
                trades: quote.trades,
              }),
            )
            dispatch(trackView(vId, qId))
          })
          .then(() => {
            dispatch(
              changeQuote(
                vId,
                qId,
                {
                  mode: getStartingMode(getState(), vId, qId),
                  isLoading: false,
                },
                { track: false },
              ),
            )
          })
          .then(() => {
            let shouldAutoSave = autosave && newQuote // only save if the quote from the server is empty

            shouldAutoSave &&
              dispatch(
                saveQuote(
                  vId,
                  qId,
                  quote.mode,
                  INTENTS.magicLink,
                  null,
                  null,
                  true,
                ),
              ).then(() => dispatch(removeFromPageState('autosave')))
          })
      })
      .catch(e => {
        console.warn('Error loading quote', qId, e)
      })
  },
  updateGlobalQuoteValue = properties => dispatch => {
    dispatch({ type: UPDATE_GLOBAL_QUOTE_VALUE, payload: properties })
  },
  processQuotes = (quotes = []) => dispatch => {
    return dispatch({
      type: MERGE_ENTITIES,
      payload: quotes.reduce(
        (ret, quote) =>
          get(quote, 'latest.id')
            ? {
                ...ret,
                [quote.latest.id]: {
                  _entityType: 'quote',
                  interactionCount: quote.interactionCount,
                  ...quote.latest,
                },
              }
            : ret,
        {},
      ),
    })
  },
  getQuotes = () => dispatch =>
    dispatch(GET.quotes()).then(r => dispatch(processQuotes(r))),
  getQuotesForVehicle = vehicleId => dispatch =>
    dispatch(GET.quotesForVehicle({}, { vehicleId })).then(r => {
      dispatch(processQuotes(r))
    }),
  getQuotesStatusesForVehicle = vehicleId => (dispatch, getState) => {
    dispatch(GET.quotesForVehicleNoLoader({}, { vehicleId })).then(
      (quotes = []) => {
        let payload = quotes.reduce((ret, q) => {
          return {
            ...ret,
            [get(q, 'latest.id')]: {
              //...getEntity(getState(), get(q, 'latest.id'), {}),
              interactionCount: q.interactionCount,
              intent: get(q, 'latest.intent'),
            },
          }
        }, {})

        dispatch({
          type: MERGE_ENTITIES,
          payload,
        })
      },
    )
  },
  getQuoteById = qId => dispatch => {
    if (!isEmpty(qId)) return

    return dispatch(GET.quote({}, { uuid: qId })).then(r => {
      dispatch(processQuotes([r]))
    })
  },
  getMarketForZip = zipcode => dispatch => {
    return dispatch(GET.marketForZip({}, { zipcode }))
  },
  validateZip = zipcode => dispatch => {
    if (zipcode.length !== 5) {
      return dispatch(addToPageState({ changeZip: 'error' }))
    } else {
      return dispatch(getMarketForZip(zipcode)).then(market => {
        if (typeof market !== 'number') {
          dispatch(addToPageState({ changeZip: 'error' }))
        }

        return market
      })
    }
  },
  getProgramsForQuote = (vId, qId) => async (dispatch, getState) => {
    if (!doesPrepencilExist(getState(), vId, qId, 'price')) return // likely missing prepencil

    dispatch(
      changeQuote(
        vId,
        qId,
        { gettingPrograms: true }, // programs: {} },
        { clean: true },
      ),
    )

    let down = getQuoteValue(getState(), vId, qId, 'down'),
      downs = getQuoteValue(getState(), vId, qId, 'downs'),
      zipcode = getQuoteValue(getState(), vId, qId, 'customerZipCode'),
      endpoint = downs.map(d => parseFloat(d.value)).includes(parseFloat(down))
        ? 'programsForQuote'
        : 'programsForCustomQuote',
      pd = cloneDeep(getQuoteForProgramPull(getState(), vId, qId))

    set(
      pd,
      'rebateInfo.Rebates',
      getRebatesForVehicle(getState(), vId, qId, {
        customer: true,
        dealer: true,
        selected: true,
      }),
    )

    return dispatch(validateZip(zipcode))
      .then(market => {
        if (typeof market === 'number' && parseInt(pd.rateMarket, 10) < 0) {
          pd.rateMarket = market
        }

        if (isNaN(parseInt(market, 10))) {
          throw new Error('Invalid ZIP')
        }
      })
      .then(() =>
        dispatch(
          POST[endpoint]({
            body: pd,
          }),
        ),
      )
      .then(response => {
        if (isEmpty(response)) throw new Error('No Response')

        dispatch(
          changeQuote(
            vId,
            qId,
            {
              mscanId: response.mscanId,
              programs: response.programs,
              programsHaveTaxes: pd.includeTaxes,
              programsHaveFees: pd.includeFees,
              gettingPrograms: false,
              dirty: false,
            },
            { clean: true },
          ),
        )
      })
      .then(() => {
        dispatch(
          changeQuote(vId, qId, {
            mode: getStartingMode(getState(), vId, qId),
          }),
        )
      })
      .then(() =>
        dispatch(
          track(
            QUOTE_REFRESHED,
            getQuoteTrackerStats(getState(), vId, qId),
            true,
          ),
        ),
      )
      .catch(e => {
        console.error(e)
        dispatch(changeQuote(vId, qId, { gettingPrograms: false }))
      })
  },
  getRebatesForQuote = (
    vId,
    qId,
    customZipCode,
    addendum = {},
    options = {},
  ) => (dispatch, getState) => {
    let zipcode =
        customZipCode || getQuoteValue(getState(), vId, qId, 'customerZipCode'),
      optionCode = getQuoteValue(getState(), vId, qId, 'vehicleOptionCode')

    return dispatch(
      GET.vehicleRebates(
        optionCode ? { queryStringParameters: { optionCode } } : {},
        { vId, zipcode },
      ),
    ).then(rebateInfo => {
      if (!rebateInfo) return dispatch({ type: 'DUPE_REBATES_CALL' })

      // get precalced rebate vals and merge with existing vals
      let precalcedRebateValues = get(rebateInfo, 'Rebates', []).reduce(
        (ret, r) => {
          let val = get(r, 'Value.Values[0].Value')
          return r.ManualValueInputRequired && val
            ? { ...ret, [r.ID]: val }
            : ret
        },
        {},
      )

      let selectedRebateIds = generateSelectedRebateIds(rebateInfo),
        existingRebateIds =
          getQuoteValue(getState(), vId, qId, 'selectedRebateIds') || [],
        rebateData = {
          rebateInfo,
          selectedRebateIds: [
            ...existingRebateIds,
            ...(options.processRebateCats
              ? getPreselectedRebates(
                  getState(),
                  vId,
                  qId,
                  addendum.selectedRebateCategories,
                  rebateInfo.Rebates,
                )
              : []),
            ...selectedRebateIds,
          ],
          selectedRebateValues: {
            ...getQuoteValue(getState(), vId, qId, 'selectedRebateValues', {}),
            ...precalcedRebateValues,
          },
        },
        merged = merge(addendum, rebateData)

      return dispatch(
        changeQuote(vId, qId, merged, {
          clean: true,
          track: true,
          ...options,
        }),
      )
    })
  },
  getProgramInfo = (vId, qId, mode) => (dispatch, getState) => {
    dispatch(changeQuote(vId, qId, { gettingPrograms: true }))

    let pd = getClonedQuote(getState(), vId, qId),
      bpk = getBestProgramKey(getState(), vId, qId, mode),
      bp = getBestProgram(getState(), vId, qId, mode)

    if (isEmpty(bp) || isEmpty(bpk)) return

    pd.mode = mode

    set(
      pd,
      'rebateInfo.Rebates',
      getRebatesForVehicle(getState(), vId, qId, {
        customer: true,
        dealer: true,
        selected: true,
      }),
    )

    REMOVE_ON_SAVE.forEach(k => delete pd[k])
    // TODO filter out initialTrade matches

    pd.programs = { [bpk]: bp }

    if (parseInt(pd.rateMarket, 10) < 0) delete pd.rateMarket

    return dispatch(POST.programInfo({ body: pd }))
      .then(response => {
        dispatch(
          changeQuote(
            vId,
            qId,
            {
              [`programs[${bpk}].programInfo`]: response,
              gettingPrograms: false,
            },
            { clean: true },
          ),
        )
      })
      .catch(() => {})
  },
  saveQuote = (
    vId,
    qId,
    mode,
    intent,
    addendum = {},
    forceProgramUpdate = false,
    silent = false,
  ) => async (dispatch, getState) => {
    dispatch({ type: 'SAVE_QUOTE' })

    let customer = get(addendum, 'customer', {}),
      zipCode = get(addendum, 'customerZipCode'),
      rateMarket = get(addendum, 'rateMarket')

    await dispatch(
      tagCustomer(vId, qId, true, {
        ...customer,
        quotes: { [vId]: qId },
        zipCode,
        rateMarket,
      }),
    )

    await dispatch(
      changeQuote(
        vId,
        qId,
        {
          ...addendum,
          includeFees: !!shouldQuoteHaveFees(getState(), vId, qId),
          includeTaxes: !!shouldQuoteHaveTaxes(getState(), vId, qId),
          status: S.saving,
          isLoading: true && !silent,
        },
        { track: true, clean: true, forceProgramUpdate },
      ),
    )

    // TODO move all this shit to a selector :')
    let pd = getClonedQuote(getState(), vId, qId)

    pd.mode = mode || 'lease'
    pd.intent = intent

    //TODO save rebates or just save selected idents?
    set(
      pd,
      'rebateInfo.Rebates',
      getRebatesForVehicle(getState(), vId, qId, {
        customer: true,
        dealer: true,
        selected: true,
      }),
    )

    pd.expirationDate = pd.expirationDate
      ? moment(pd.expirationdate).unix()
      : moment()
          .add(pd.expirationDays, 'days')
          .unix()

    pd.quotedPayment = getBestPayment(getState(), vId, qId, pd.mode)
    pd.quotedBankCode = getBestProgramValue(
      getState(),
      vId,
      qId,
      pd.mode,
      'Lender',
    )
    pd.quotedProgramDescription = getBestProgramValue(
      getState(),
      vId,
      qId,
      pd.mode,
      'ProgramDescription',
    )

    if (!getIsLoggedIn(getState()) && getEnableDealerPolicyLink(getState())) {
      pd.dealerPolicyLink = {
        rooftopName: getRooftopName(getState()),
        redirectUrl: window.location.href,
      }
    }

    REMOVE_ON_SAVE.forEach(k => delete pd[k])

    return dispatch(doSaveQuote(vId, qId, pd))
      .then(({ shortCode, fastpass }) => {
        dispatch(getQuotesForVehicle(vId))
        dispatch(getQuoteById(qId))
        dispatch(
          track(
            QUOTE_SAVED,
            {
              intent,
              appointment: pd.appointment,
              customerComments: pd.customerComments,
              ...getQuoteTrackerStats(getState(), vId, qId),
            },
            true,
          ),
        )

        return dispatch(
          changeQuote(
            vId,
            qId,
            { shortCode, intent, fastpass, status: S.ready, isLoading: false },
            { track: true, clean: true, dirty: false },
          ),
        )
      })
      .then(() => {
        !silent && dispatch(setNewStartingQuoteId(vId))
      })
  },
  doSaveQuote = (vId, qId, addendum) => (dispatch, getState) => {
    let quote = {
      ...getClonedQuote(getState(), vId, qId),
      ...addendum,
    }

    return dispatch(POST.saveQuote({ body: quote }))
  },
  copyVersionToLatest = (id, txn) => (dispatch, getState) => {
    return dispatch({
      type: MERGE_ENTITIES,
      payload: {
        [id]: getQuoteByVersion(getState(), id, txn),
      },
    })
  },
  addItem = (vId, qId, collection, item) => (dispatch, getState) => {
    let qd = getClonedQuote(getState(), vId, qId)

    dispatch(
      changeQuote(vId, qId, {
        [collection]: [
          ...qd[collection],
          item ||
            cloneDeep({
              ...INITIAL_TYPES[collection],
              key: uuid(),
            }),
        ],
      }),
    )

    dispatch(
      track(
        QUOTE_ADDITION,
        {
          collection,
          ...getQuoteTrackerStats(getState(), vId, qId),
        },
        true,
      ),
    )
  },
  removeItem = (vId, qId, idx, collection) => (dispatch, getState) => {
    let qd = getClonedQuote(getState(), vId, qId),
      removedItem = qd[collection][idx]

    qd[collection].splice(idx, 1)

    dispatch(changeQuote(vId, qId, { [collection]: qd[collection] }))

    dispatch(
      track(
        QUOTE_REMOVAL,
        {
          ...getQuoteTrackerStats(getState(), vId, qId),
          collection,
          removedItem,
        },
        true,
      ),
    )
  },
  updateQuoteStatus = (vId, qId, status, extraProps = {}) => dispatch =>
    dispatch(changeQuote(vId, qId, { status, ...extraProps })),
  forceRoll = (vId, qId, mode) => dispatch => {
    return dispatch(getRollResults(vId, qId, mode))
      .then(cash => {
        if (!cash) return

        return dispatch(
          changeQuote(
            vId,
            qId,
            {
              down: Math.ceil(cash),
            },
            { dirty: true, forceProgramUpdate: true },
          ),
        )
      })
      .then(() => dispatch(saveQuote(vId, qId, mode, INTENTS.rolled)))
  },
  getRollResults = (vId, qId, mode) => (dispatch, getState) => {
    let quote = getClonedQuote(getState(), vId, qId),
      proc = r => {
        let { cash } = r

        dispatch(
          updateQuoteStatus(vId, qId, null, {
            rollResults: {
              cash: mode === 'loan' ? get(cash, 'value', 0) : cash,
            },
          }),
        )

        return mode === 'loan' ? get(cash, 'value', 0) : cash
      }

    set(
      quote,
      'rebateInfo.Rebates',
      getRebatesForVehicle(getState(), vId, qId, {
        customer: true,
        dealer: true,
        selected: true,
      }),
    )

    dispatch(updateQuoteStatus(vId, qId, STATUS.rolling, { rollResults: null }))

    delete quote._versions

    return dispatch(
      POST.rollResults({
        body: {
          ...quote,
          mode,
          programs: {
            [getBestProgramKey(getState(), vId, qId, mode)]: getBestProgram(
              getState(),
              vId,
              qId,
              mode,
            ),
          },
        },
      }),
    )
      .then(proc)
      .catch(proc)
  },
  toggleRebateCategory = (vId, qId, category, parent = null) => (
    dispatch,
    getState,
  ) => {
    let selectedRebateCategories = getQuoteValue(
        getState(),
        vId,
        qId,
        'selectedRebateCategories',
        [],
      ),
      selectedRebateIds = getSelectedRebateIds(getState(), vId, qId),
      cat = { ...category, parent: parent },
      catIndex = selectedRebateCategories.findIndex(c => isEqual(cat, c)),
      alreadySelected = catIndex !== -1,
      rebateIdsToSelect = getRebatesForCategory(getState(), vId, qId, cat).map(
        r => r.ID,
      )

    if (alreadySelected) selectedRebateCategories.splice(catIndex, 1)
    else selectedRebateCategories = [...selectedRebateCategories, cat]

    if (alreadySelected)
      selectedRebateIds = selectedRebateIds.filter(
        s => !rebateIdsToSelect.includes(s),
      )
    else selectedRebateIds = uniq([...selectedRebateIds, ...rebateIdsToSelect])

    dispatch(
      changeQuote(vId, qId, { selectedRebateCategories, selectedRebateIds }),
    )
    dispatch(tagCustomer(vId, qId, true, { selectedRebateCategories }))
  },
  toggleRebate = (vId, qId, id, trackClick = true) => (dispatch, getState) => {
    let rebateIds = getQuoteValue(getState(), vId, qId, 'selectedRebateIds'),
      alreadySelected = rebateIds.includes(id),
      newIds = alreadySelected ? without(rebateIds, id) : [...rebateIds, id],
      rebate = getRebateById(getState(), vId, qId, id)

    dispatch(changeQuote(vId, qId, { selectedRebateIds: newIds }))

    trackClick &&
      dispatch(
        track(
          alreadySelected ? QUOTE_REMOVAL : QUOTE_ADDITION,
          {
            ...getQuoteTrackerStats(getState(), vId, qId),
            rebate,
            collection: 'rebates',
          },
          true,
        ),
      )
  },
  setRebateValue = (vId, qId, rId, value) => (dispatch, getState) => {
    let selectedRebateValues = getQuoteValue(
      getState(),
      vId,
      qId,
      'selectedRebateValues',
      [],
    )

    value
      ? (selectedRebateValues[rId] = value)
      : delete selectedRebateValues[rId]

    dispatch(changeQuote(vId, qId, { selectedRebateValues }))
  },
  setShortCodeQuoteId = qId => dispatch => {
    dispatch({ type: SET_SHORT_CODE_QUOTE_ID, payload: qId })
  },
  trackSection = (vId, qId, section, open) => (dispatch, getState) => {
    dispatch(
      track(
        QUOTE_CHANGED,
        {
          changes: [{ key: section, to: open ? 'open' : 'closed' }],
          ...getQuoteTrackerStats(getState(), vId, qId),
        },
        true,
      ),
    )
  },
  trackReaction = (vId, qId, reaction) => (dispatch, getState) => {
    dispatch(
      track(QUOTE_REACTION, {
        changes: [{ key: reaction }],
        ...getQuoteTrackerStats(getState(), vId, qId),
      }),
    )
  },
  trackAction = (vId, qId, action) => (dispatch, getState) => {
    dispatch(
      track(QUOTE_CHANGED, {
        changes: [{ key: action }],
        ...getQuoteTrackerStats(getState(), vId, qId),
      }),
    )
  },
  toggleLock = (vId, qId, lock, value = 0) => (dispatch, getState) => {
    let lockValue = getQuoteValue(getState(), vId, qId, `locks.${lock}`)

    dispatch(
      changeQuote(vId, qId, {
        [`locks.${lock}`]: value === lockValue ? null : value,
      }),
    )
  },
  toggleOptionCode = (vId, qId, code) => (dispatch, getState) => {
    let selectedOptionCodes = (
        getQuoteValue(getState(), vId, qId, 'vehicleOptionCode', '') || ''
      ).split(','),
      newCodes = selectedOptionCodes.includes(code)
        ? without(selectedOptionCodes, code)
        : [...selectedOptionCodes, code]

    dispatch(changeQuote(vId, qId, { vehicleOptionCode: newCodes.join() }))
  }
